import { IComponentUpdatePlugin, IComponentUpdateV2Payload } from './core/types'

import { ConsoleLoggerPlugin } from './plugins/ConsoleLoggerPlugin'
import { StateManagerPlugin } from './plugins/StateManagerPlugin'
import Vue from 'vue'
import { getComponentName } from './utils'

export class ComponentUpdateTracker {
  private static instance: ComponentUpdateTracker
  private plugins: Map<string, IComponentUpdatePlugin> = new Map()
  private enabled: boolean = localStorage.getItem("tracker-enabled") === "true"

  // Add new class properties
  private blacklistedComponents: string[] = ['PerformanceDebugger', 'PerformanceMetrics', 'UpdatesList', 'ComponentUpdatesDebugger']
  private whitelistedComponents: string[] = ['SmartList', 'RecordsNav']
  private useWhitelist: boolean = false

  private constructor() {
    this.initializePlugins()
    if(this.enabled) {
      this.setupVueMixin()
      console.log('%c[@component-updates] Enabled', 'color: green; font-weight: bold;')
    }
  }

  static getInstance(): ComponentUpdateTracker {
    if (!this.instance) {
      this.instance = new ComponentUpdateTracker()
    }
    return this.instance
  }

  isEnabled(): boolean {
    return this.enabled
  }

  enable(): void {
    if (this.enabled) return
    this.enabled = true
    this.setupVueMixin()
    console.log('%c[@component-updates] Enabled', 'color: green; font-weight: bold;')
  }

  disable(): void {
    this.enabled = false
    this.plugins.forEach(plugin => {
      if (typeof plugin.disable === 'function') {
        plugin.disable()
      }
    })
  }

  getPlugin<T extends IComponentUpdatePlugin>(id: string): T | undefined {
    return this.plugins.get(id) as T
  }

  private initializePlugins(): void {
    const stateManager = new StateManagerPlugin()
    const consoleLogger = new ConsoleLoggerPlugin()

    this.plugins.set(stateManager.id, stateManager as unknown as IComponentUpdatePlugin)
    this.plugins.set(consoleLogger.id, consoleLogger as unknown as IComponentUpdatePlugin)
  }

  private safeClone(obj: any) {
    if (!obj) return obj

    // Do a shallow clone for performance and to avoid recursion
    if (Array.isArray(obj)) {
      return [...obj]
    }
    return { ...obj }
  }

  private shouldTrackComponent(componentName: string): boolean {
    if (!componentName || componentName === 'Anonymous') return false

    // Skip components that start with any of the blacklisted prefixes
    const isBlacklisted = this.blacklistedComponents.some(
      prefix => componentName.startsWith(prefix) || componentName.includes(prefix)
    )

    if (isBlacklisted) return false

    if (this.useWhitelist) {
      return this.whitelistedComponents.includes(componentName)
    } else {
      // Additional checks for common library patterns
      if (componentName.match(/^[A-Z][a-z]+(?:Picker|Dialog|Popup|Modal|List|Table)$/)) return false
      return true
    }
  }

  private setupVueMixin(): void {
    const tracker = this
    const componentsIndexMapper = new Map<string, number>()

    Vue.mixin({
      created() {
        const fileName = getComponentName(this)
        if (!tracker.shouldTrackComponent(fileName)) return

        componentsIndexMapper.set(
          fileName,
          componentsIndexMapper.has(fileName) ? componentsIndexMapper.get(fileName)! + 1 : 0
        )

        this.__shouldTrack = true
        this.__fileName = fileName
        this.__propChanges = {}
        this.__dataChanges = {}
        this.__componentIndex = componentsIndexMapper.get(fileName)!
        this.__updateCount = 0

        if (this.$data) {
          Object.keys(this.$data).forEach(key => {
            this.__propChanges[key] = {
              oldValue: this.$data[key],
              newValue: this.$data[key],
              changed: false,
            }
            this.$watch(
              () => this.$data[key],
              (newVal, oldVal) => {
                this.__dataChanges[key] = {
                  oldValue: oldVal,
                  newValue: newVal,
                  changed:  oldVal !== newVal,
                }
              }
            )
          })
        }

        if (this.$props) {
          Object.keys(this.$props).forEach(key => {
            this.__propChanges[key] = {
              oldValue: this.$props[key],
              newValue: this.$props[key],
              changed: false,
            }
            this.$watch(
              () => this.$props[key],
              (newVal, oldVal) => {
                this.__propChanges[key] = {
                  oldValue: oldVal,
                  newValue: newVal,
                  changed:  oldVal !== newVal,
                }
              }
            )
          })
        }
      },

      beforeUpdate() {
        if (!this.__shouldTrack) return
        this.__renderStartTime = performance.now()
      },

      updated() {
        if (!this.__shouldTrack) return

        const updateDuration = this.__renderStartTime ? performance.now() - this.__renderStartTime : 0
        this.__updateCount += 1

        const updateData: IComponentUpdateV2Payload = {
          componentName: `${this.__fileName}(${this.__componentIndex})`,
          filePath: this.$options.__file,
          updateCount: this.__updateCount,
          htmlElement: this.$el,
          lastUpdateTime: Date.now(),
          updateDuration,
          changedProps: {
            duration: updateDuration,
            values:this.__propChanges
          },
          changedData:{
            duration: updateDuration,
            values: this.__dataChanges,
          }
        }
        tracker.notifyPlugins('updateReRenderHistory', updateData)
      },

      destroyed() {
        if (!this.__shouldTrack) return
        tracker.notifyPlugins('removeComponentFromHistory', this.__fileName);
      }
    })
  }

  private notifyPlugins(event: string, data: any): void {
    if (!this.enabled) return

    this.plugins.forEach(plugin => {
      if (plugin.enabled && plugin[event]) {
        plugin[event](data)
      }
    })
  }
}